type InputDevices = {
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
  hasAudioInputDevices: boolean;
  hasVideoInputDevices: boolean;
};

export async function getDeviceInfo(): Promise<InputDevices> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const videoInputDevices = devices.filter((device) => device.kind === 'videoinput');

  const backKeywords = ['back', 'rear', 'arka', '后', 'trasera', 'wide', 'rück', 'posterior', '뒤', 'задняя'];

  // Preferred back camera names in different languages
  const preferredBackNames = [
    'back camera', // English
    'arka kamera', // Turkish
    'cámara trasera', // Spanish
    'caméra arrière', // French
    'rückkamera', // German
    'fotocamera posteriore', // Italian
    'câmera traseira', // Portuguese
    '后摄像头', // Chinese Simplified
    '後置攝像頭', // Chinese Traditional
    '背面カメラ', // Japanese
    '후면 카메라', // Korean
    'задняя камера', // Russian
  ];

  // Separate front and back cameras
  const frontCameras: MediaDeviceInfo[] = [];
  const backCameras: MediaDeviceInfo[] = [];

  videoInputDevices.forEach((device) => {
    const label = device.label.toLowerCase().trim();

    const isBackCamera = backKeywords.some((keyword) => label.includes(keyword));

    if (isBackCamera) {
      backCameras.push(device);
    } else {
      frontCameras.push(device);
    }
  });

  let selectedBackCamera: MediaDeviceInfo[] = [];

  selectedBackCamera =
    backCameras.length > 0
      ? [
          backCameras.find((device) => preferredBackNames.some((name) => device.label.toLowerCase().includes(name))) ||
            backCameras[0],
        ]
      : [];

  return {
    audioInputDevices: devices.filter((device) => device.kind === 'audioinput'),
    videoInputDevices: [...frontCameras, ...selectedBackCamera],
    audioOutputDevices: devices.filter((device) => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some((device) => device.kind === 'audioinput'),
    hasVideoInputDevices: [...frontCameras, ...selectedBackCamera].length > 0,
  };
}

export async function isPermissionDenied(name: 'camera' | 'microphone'): Promise<boolean> {
  // Query the Permissions API instead of opening a real stream. The previous
  // implementation called getUserMedia here and immediately stopped the
  // tracks, which leaves the device briefly held by the browser/OS. The
  // createLocalTracks call that runs straight afterwards then intermittently
  // failed with NotReadableError, and the user was left with no microphone
  // track at all - an unmute button that silently does nothing.
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return false;
  }

  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state === 'denied';
  } catch {
    // Safari and Firefox do not expose the camera/microphone descriptors.
    // Assume granted and let createLocalTracks report the real failure,
    // rather than guessing and skipping acquisition entirely.
    return false;
  }
}

export const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();
